import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { QInvestingIcon } from "./icon";
import { cn } from "../../lib/utils";

export const INVESTOR_TIPS = [
  {
    title: "Time in the market",
    body: "Missing the best few trading days each decade can erase years of compounded gains. Staying invested usually beats timing exits.",
  },
  {
    title: "Diversify with intent",
    body: "Spread risk across sectors and regions — but know why each holding is there. Random baskets aren't strategy.",
  },
  {
    title: "Fees compound too",
    body: "A 1% annual fee can quietly take a large bite out of long-horizon returns. Prefer low-cost exposure when it fits your thesis.",
  },
  {
    title: "Rebalance, don't chase",
    body: "Periodic rebalancing locks in discipline. Chasing last quarter's winners often buys high and sells low.",
  },
  {
    title: "Write the thesis down",
    body: "If you can't explain why you own it in one sentence, you may not own a conviction — just a ticker.",
  },
  {
    title: "Cash is a position",
    body: "Idle cash has a cost of opportunity, but dry powder for planned buys is a feature, not a failure.",
  },
  {
    title: "Ignore the noise cycle",
    body: "Headlines optimize for attention. Your portfolio should optimize for your horizon, risk, and constraints.",
  },
  {
    title: "Measure what matters",
    body: "Track after-fee, after-tax progress against your plan — not against a neighbor's highlight reel.",
  },
] as const;

interface PageLoaderProps {
  className?: string;
  /** Optional label announced to assistive tech. */
  label?: string;
  /** Full-viewport gate (auth / route) vs inline. */
  fullScreen?: boolean;
}

/**
 * Branded full-page loader with rotating investor tips and ledger-line motion.
 * Replaces the plain blue spinner on auth and route gates.
 */
export function PageLoader({
  className,
  label = "Loading",
  fullScreen = true,
}: PageLoaderProps) {
  const reduceMotion = useReducedMotion();
  const startIndex = useMemo(
    () => Math.floor(Math.random() * INVESTOR_TIPS.length),
    [],
  );
  const [tipIndex, setTipIndex] = useState(startIndex);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const id = window.setInterval(() => {
      setTipIndex((i) => (i + 1) % INVESTOR_TIPS.length);
    }, 5200);
    return () => window.clearInterval(id);
  }, [reduceMotion]);

  const tip = INVESTOR_TIPS[tipIndex];

  return (
    <div
      className={cn(
        "otr-app relative flex items-center justify-center overflow-hidden",
        fullScreen ? "min-h-screen w-full" : "min-h-[240px] w-full py-12",
        className,
      )}
      style={{
        background:
          "radial-gradient(120% 80% at 50% -10%, oklch(0.92 0.02 85), transparent 55%), linear-gradient(165deg, oklch(0.968 0.008 92) 0%, oklch(0.945 0.012 92) 48%, oklch(0.93 0.016 255 / 0.35) 100%)",
      }}
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
      data-testid="page-loader"
    >
      {/* Soft ledger grid */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(oklch(0.245 0.012 70 / 0.06) 1px, transparent 1px), linear-gradient(90deg, oklch(0.245 0.012 70 / 0.06) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage:
            "radial-gradient(ellipse 70% 60% at 50% 45%, black 20%, transparent 75%)",
        }}
      />

      <div className="relative z-10 mx-auto flex w-full max-w-lg flex-col items-center px-6 text-center">
        <motion.div
          className="mb-5 flex items-center gap-3"
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.2, 0.7, 0.2, 1] }}
        >
          <QInvestingIcon className="h-10 w-10 text-[oklch(0.245_0.012_70)]" />
          <span
            className="text-3xl font-semibold tracking-tight text-[oklch(0.245_0.012_70)]"
            style={{ fontFamily: '"Plus Jakarta Sans", "Public Sans", system-ui, sans-serif' }}
          >
            Quantum
          </span>
        </motion.div>

        {/* Animated performance line */}
        <div className="relative mb-8 h-16 w-full max-w-xs" aria-hidden>
          <svg
            viewBox="0 0 320 64"
            className="h-full w-full overflow-visible"
            fill="none"
          >
            <motion.path
              d="M4 48 C 40 48, 48 20, 80 28 S 120 56, 160 36 S 220 8, 260 22 S 300 40, 316 18"
              stroke="oklch(0.52 0.11 158)"
              strokeWidth="2.5"
              strokeLinecap="round"
              initial={
                reduceMotion
                  ? { pathLength: 1, opacity: 1 }
                  : { pathLength: 0, opacity: 0.35 }
              }
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{
                duration: reduceMotion ? 0 : 1.6,
                ease: [0.2, 0.7, 0.2, 1],
                repeat: reduceMotion ? 0 : Infinity,
                repeatType: "loop",
                repeatDelay: 0.85,
              }}
            />
            <motion.circle
              r="4.5"
              fill="oklch(0.245 0.012 70)"
              initial={reduceMotion ? false : { opacity: 0 }}
              animate={
                reduceMotion
                  ? { cx: 316, cy: 18, opacity: 1 }
                  : {
                      cx: [4, 80, 160, 260, 316],
                      cy: [48, 28, 36, 22, 18],
                      opacity: 1,
                    }
              }
              transition={{
                duration: 2.45,
                ease: "easeInOut",
                repeat: reduceMotion ? 0 : Infinity,
                repeatDelay: 0.85,
              }}
            />
          </svg>
        </div>

        <p
          className="mb-6 text-xs font-medium uppercase tracking-[0.22em] text-[oklch(0.45_0.010_70)]"
          style={{ fontFamily: '"Public Sans", system-ui, sans-serif' }}
        >
          Preparing your ledger
        </p>

        <div className="min-h-[7.5rem] w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={tipIndex}
              initial={reduceMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
              className="rounded-sm border border-[oklch(0.245_0.012_70/0.12)] bg-[oklch(0.98_0.006_92/0.72)] px-5 py-4 text-left shadow-[0_12px_40px_-24px_oklch(0.245_0.012_70/0.35)] backdrop-blur-sm"
            >
              <p
                className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[oklch(0.52_0.11_158)]"
                style={{ fontFamily: '"Public Sans", system-ui, sans-serif' }}
              >
                Investor tip
              </p>
              <h2
                className="mb-1.5 text-lg font-semibold text-[oklch(0.245_0.012_70)]"
                style={{
                  fontFamily:
                    '"Plus Jakarta Sans", "Public Sans", system-ui, sans-serif',
                }}
              >
                {tip.title}
              </h2>
              <p
                className="text-sm leading-relaxed text-[oklch(0.45_0.010_70)]"
                style={{ fontFamily: '"Public Sans", system-ui, sans-serif' }}
              >
                {tip.body}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Tip progress dots */}
        <div className="mt-5 flex gap-1.5" aria-hidden>
          {INVESTOR_TIPS.map((_, i) => (
            <span
              key={i}
              className={cn(
                "h-1 w-1 rounded-full transition-colors duration-300",
                i === tipIndex
                  ? "bg-[oklch(0.245_0.012_70)]"
                  : "bg-[oklch(0.245_0.012_70/0.2)]",
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default PageLoader;
